import React, { Component } from 'react';
import { Location } from 'history';
import { RouteComponentProps } from 'react-router';
import b from 'b_';

import './publication-list.css';

import { Publication } from '@interfaces/publications-page';
import Theme from '@interfaces/themes';
import { ShowMore, PaginationWrapperProps } from '@components/pagination-wrapper';
import { fetchTheme, fetchThemes } from 'client/ducks/themes/actions';
import { fetchPublicationsPage } from 'client/ducks/publications/actions';
import { AsyncFetchDataProps } from '@interfaces/async-fetch-data';
import { PublicationRequestParams } from '@interfaces/publication-request-params';
import { formatQuery, parseQuery } from 'client/helpers/url';
import PaginationMeta from '@interfaces/pagination-meta';
import ThemePageHeader from '@components/theme-page-header';
import PublicationsPageHeader from '@components/publications-page-header';
import { BreadcrumbsStub } from '@components/breadcrumbs';
import { ThemesStub } from '@components/themes-widget';
import PageMeta from '@components/page-meta';
import Rect from '@components/rect';
import MobilePublicationsPageHeader from '@components/mobile-publications-page-header';
import PublicationItem from 'client/components/publication-item';
import PublicationItemStub from '@components/publication-item/publication-item.stub';

import pageConfig from './page-config.json';

const class_ = b.with('publication-list');

export interface DispatchProps {
    fetchPage(params: PublicationRequestParams): void;
    fetchTheme(themSlug: string): void;
    fetchThemes(): void;
}

export interface StateProps {
    hasMore: boolean;
    rows: Publication[];
    meta: PaginationMeta;
    theme: Theme;
    themes: Theme[];
    isLoading: boolean;
    params: PublicationRequestParams;
    isLoadingTheme: boolean;
    isLoadingThemes: boolean;
}

export interface SizeProps {
    isDesktop: boolean;
}

export type Props = SizeProps & StateProps & DispatchProps & PaginationWrapperProps & RouteComponentProps<any>;

export default class PublicationsListPage extends Component<Props> {
    public static asyncFetchData({ dispatch, location }: AsyncFetchDataProps) {
        const params = PublicationsListPage.getRequestParams(location);
        const query = parseQuery(location.search);
        const themeSlug = query.get('themeSlug');

        dispatch(fetchPublicationsPage(params));
        dispatch(fetchThemes());

        if (themeSlug) {
            dispatch(fetchTheme(themeSlug));
        }
    }

    public static getRequestParams(location: Location, page: number = 1): PublicationRequestParams {
        const query = parseQuery(location.search);
        const year = query.get('year');
        const themeSlug = query.get('themeSlug');
        const params: PublicationRequestParams = { page };

        if (year) {
            params.year = parseInt(year, 10);
        }

        if (themeSlug) {
            params.themeSlug = themeSlug;
        }

        return params;
    }

    get year() {
        const query = parseQuery(this.props.location.search);

        return query.get('year');
    }

    get slug() {
        const query = parseQuery(this.props.location.search);

        return query.get('themeSlug');
    }

    public componentDidMount() {
        this.loadPageData();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.location.search !== this.props.location.search) {
            this.loadPageData(true);
        }
    }

    /* tslint:disable:cyclomatic-complexity */
    public loadPageData(force = false) {
        const { themes, theme, isDesktop, params, rows } = this.props;
        const queryNotChanged = (!this.slug || params.themeSlug === this.slug)
            && (!this.year || params.year === parseInt(this.year, 10));

        if (rows.length > 0 && !force && queryNotChanged) {
            return;
        }

        if (this.slug && (!theme || theme.slug !== this.slug)) {
            this.props.fetchTheme(this.slug);
        }
        if (!isDesktop || !themes.length) {
            this.props.fetchThemes();
        }
        this.fetchPublications();
    }

    public updateUrl(params: PublicationRequestParams = {}) {
        const { location, history } = this.props;
        const currentParams = PublicationsListPage.getRequestParams(location);
        const newParams = { ...currentParams, ...params };
        const { year, themeSlug } = newParams;
        const query = formatQuery({ year, themeSlug });

        history.push(`/publications${query}`);
    }

    public fetchPublications(params: PublicationRequestParams = {}) {
        const { location, fetchPage } = this.props;
        const currentParams = PublicationsListPage.getRequestParams(location);
        const newParams = { ...currentParams, ...params };

        fetchPage(newParams);
    }

    public onShowMoreClick = () => {
        const { meta } = this.props;
        const { pageNumber } = meta;

        this.fetchPublications({ page: 1 + pageNumber });
    };

    public onYearChange = (year?: number) => {
        this.updateUrl({ year });
    };

    public onThemeChange = (themeSlug?: string) => {
        this.updateUrl({ themeSlug, year: undefined });
    };

    public renderPublications() {
        const { isLoading, hasMore, params, rows } = this.props;

        if (isLoading && params.page === 1) {
            return (
                <div>
                    <PublicationItemStub itemsCount={5} type="horizontal" />
                    <Rect height={48} />
                </div>
            );
        }

        if (!rows.length) {
            return (
                <div className={class_('not-found')}>
                    Nothing found that matches your search criteria. Try expanding your search.
                </div>
            );
        }

        return (
            <div>
                {rows.map(event =>
                    <PublicationItem
                        type="horizontal"
                        key={event.id}
                        entity={event}
                    />
                )}
                {hasMore && (
                    <ShowMore
                        onClick={this.onShowMoreClick}
                        isLoading={isLoading}
                    />
                )}
            </div>
        );
    }

    public renderHeaderWithThemes(year: string | null) {
        const { themes, isLoadingThemes } = this.props;

        if (isLoadingThemes || !themes) {
            return (
                <div>
                    <Rect width={150} height={48} type="black" />
                    <ThemesStub itemsCount={6} />
                    <Rect height={48} />
                </div>
            );
        }

        return (
            <PublicationsPageHeader
                year={year}
                themes={themes}
                onThemeChange={this.onThemeChange}
                onYearChange={this.onYearChange}
            />
        );
    }

    public renderThemeHeader(year: string | null) {
        const { theme, isLoadingTheme } = this.props;

        if (isLoadingTheme || !theme || !theme.title) {
            return (
                <div>
                    <BreadcrumbsStub />
                    <Rect height={273} type="black" />
                    <br />
                </div>
            );
        }

        return (
            <ThemePageHeader
                year={year}
                theme={theme}
                onYearChange={this.onYearChange}
            />
        );
    }

    /* tslint:disable:cyclomatic-complexity*/
    public render() {
        const { location, theme, isDesktop } = this.props;
        const query = parseQuery(location.search);
        const year = query.get('year');
        const themeSlug = query.get('themeSlug');

        let meta: any;
        if (theme && theme.title && themeSlug) {
            const { title, description } = theme;
            meta = (
                <PageMeta
                    title={`${title} - ${pageConfig.meta.title}`}
                    description={description}
                />
            );
        } else {
            meta =  <PageMeta title={pageConfig.meta.title} description={pageConfig.meta.description} />;
        }

        let head: any;
        if (isDesktop) {
            head = themeSlug
                ? this.renderThemeHeader(year)
                : this.renderHeaderWithThemes(year);
        } else {
            const { themes } = this.props;

            head = (
                <MobilePublicationsPageHeader
                    themes={themes}
                    selectedTheme={themeSlug}
                    year={year}
                    onThemeChange={this.onThemeChange}
                    onYearChange={this.onYearChange}
                />
            );
        }

        return (
            <div className={`block ${class_({ 'theme-page': Boolean(themeSlug) })}`}>
                {meta}
                {head}
                {this.renderPublications()}
            </div>
        );
    }
}
